import * as aq from 'arquero';
import { z } from 'zod';

import type { SortDirection } from '../../../../schema/defs.ts';
import type { SourcePos } from '../../ast.ts';
import type { EvalResult } from '../../env.ts';
import {
  SCALAR_HINT_KINDS,
  ShapeError,
  SortKeyError,
  TypeMismatchError,
  UnsortableColumnTypeError,
} from '../../errors.ts';
import { type AstInput, astShape, embeddedString, nodePos, opCallPos } from '../../normalize.ts';
import { type ColumnRefInArg, opMeta, type SpecialCtx } from '../../op-meta.ts';
import { elemPath, paramPath } from '../../path.ts';
import { compileProjectorLambda, compileSortKeys, type SortKey, withTable } from '../../runtime/arquero.ts';
import { astArg } from '../../schema/ast-codec.ts';
import { exprColRefs } from '../../schema/check.ts';
import { passThroughSchema } from '../../schema/infer.ts';
import { describeColumnType, isTemporalColumn, scalarSubfields } from '../../schema/types.ts';
import { type Cell, describeValueKind, LambdaValue, type MaterializedDataset } from '../../value.ts';
import { dropSynthetic, resolveDsArg } from './helpers.ts';

/**
 * Enumerate column refs used by a single sort-key arg — keyword col-refs
 * directly (`:col`, `[:col]`, `[:col :desc]`) and column refs inside the
 * body of a computed-key lambda (`#(expr)`, `[#(expr) :desc]`). SSOT for
 * sort's `referencedColumns` hook; tolerant of malformed shapes (returns
 * `[]` rather than throwing — runtime `apply` raises `ShapeError` later).
 * Mirrors `classifyKey`'s structural decoding but in a non-throwing,
 * ref-enumerating shape.
 */
const sortKeyRefs = (keyAst: AstInput, keyPath: string): readonly ColumnRefInArg[] => {
  const shape = astShape(keyAst);
  if (shape.kind === 'keyword') {
    return [{ name: shape.name, path: keyPath }];
  }
  if (shape.kind === 'vector') {
    const first = shape.elements[0];
    if (first === undefined) {
      return [];
    }
    const firstShape = astShape(first);
    const firstPath = elemPath(keyPath, 0);
    if (firstShape.kind === 'keyword') {
      return [{ name: firstShape.name, path: firstPath }];
    }
    if (firstShape.kind === 'opCall' && firstShape.call.name === 'fn') {
      const body = firstShape.call.params?.[1];
      return body === undefined ? [] : exprColRefs(body, `${firstPath}/params/1`);
    }
    return [];
  }
  if (shape.kind === 'opCall' && shape.call.name === 'fn') {
    const body = shape.call.params?.[1];
    return body === undefined ? [] : exprColRefs(body, `${keyPath}/params/1`);
  }
  return [];
};
/**
 * `(sort ds [:col1] [:col2 :desc] #(<expr>) …)` — order rows by one or
 * more keys. Each key is either:
 *
 *   - a 1-or-2-tuple `[:col]` / `[:col :asc|:desc]` — keyword column
 *     reference, optional direction (default ascending);
 *   - a 1-or-2-tuple `[#(<expr>)]` / `[#(<expr>) :desc]` — computed
 *     key (function lifted to a synthetic column then sorted);
 *   - a bare `#(<expr>)` — computed key with default ascending
 *     direction (sugar for `[#(<expr>) :asc]`).
 *
 * Direction tokens accept either keyword (`:asc`/`:desc`) or string
 * (`"asc"`/`"desc"`) forms — LLM-friendly leniency per the engine's
 * `keywordOrEnumAstSchema` policy.
 *
 * Errors:
 *
 *   - First arg not a `Dataset` → `TypeMismatchError`.
 *   - Key tuple has 0 or 3+ elements → `ShapeError`.
 *   - First tuple element isn't a keyword or fn-expr → `ShapeError`.
 *   - Second tuple element isn't `:asc`/`:desc` (or `"asc"`/`"desc"`)
 *     → `ShapeError`.
 *   - Keyword column doesn't exist on `ds.schema` → `TypeMismatchError`.
 *   - Column type is unsortable (struct/array/nested-dataset) →
 *     `UnsortableColumnTypeError`.
 *
 * A `LazyDataset` input is forced via `ctx.materialize` before the
 * ordering runs. Synthetic columns created for computed keys are
 * dropped from the result so they never leak downstream.
 */
export const sort = z
  .object({
    name: z.literal('sort'),
    params: z.tuple([astArg('<dataset>'), astArg('<sort-key>')]).rest(astArg('<sort-key>')),
  })
  .describe(
    'Order rows by one or more keys. Each key is a `[:col]` / `[:col :desc]` tuple or a `#(<expr>)` computed-key function.',
  )
  .register(opMeta, {
    // Special evaluation: a key arg can be either a vector AST (tuple
    // form) or a fn-call AST (lambda) — neither pre-evaluates to a
    // useful Value via eager dispatch, so we walk the raw AST here and
    // either extract the keyword col name OR compile the lambda body
    // against the threaded dataset.
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(-> wells (sort [:revenue :desc] [:name]))' },
    apply: async (data, ctx: SpecialCtx): Promise<EvalResult> => {
      const callPos = opCallPos(ctx.spans, ctx.path);
      const [dsAst, ...keyAsts] = data.params as readonly [AstInput, ...AstInput[]];
      const { value: dsValue } = await ctx.evalNode(dsAst, ctx.env, paramPath(ctx.path, 0));
      const concrete = await resolveDsArg(dsValue, '(sort ds <key> …)', ctx);

      // Sequential async reduce: each computed-key arg may extend the
      // dataset with a synthetic column, so the next iteration must
      // see that updated state. Sort against the accumulated dataset;
      // drop synthetics from the output so they never escape the verb.
      type Folded = {
        readonly working: MaterializedDataset;
        readonly synthetics: readonly string[];
        readonly keys: readonly SortKey[];
      };
      const folded = await keyAsts.reduce<Promise<Folded>>(
        async (accP, keyAst, i) => {
          const acc = await accP;
          const keyPath = paramPath(ctx.path, i + 1);
          const classified = classifyKey(keyAst, keyPath, ctx);
          if (classified.kind === 'col') {
            const baseKey = buildKey(classified.column, classified.direction, acc.working, callPos);
            return { working: acc.working, synthetics: acc.synthetics, keys: [...acc.keys, baseKey] };
          }
          const synth = `__lifted__sort__${acc.synthetics.length}`;
          const rowFn = await compileProjectorLambda(
            classified.lambda,
            1,
            ctx.ops,
            ctx.identities,
            ctx.spans,
            classified.fnPath,
          );
          const lifted = withTable(acc.working, (t) => t.derive({ [synth]: aq.escape(rowFn) }));
          const baseKey = buildKey(synth, classified.direction, lifted, callPos);
          return {
            working: lifted,
            synthetics: [...acc.synthetics, synth],
            keys: [...acc.keys, baseKey],
          };
        },
        Promise.resolve({ working: concrete, synthetics: [], keys: [] }),
      );

      const sorted = withTable(folded.working, (t) => t.orderby(...compileSortKeys(folded.keys)));
      const dropped = dropSynthetic(sorted, folded.synthetics);
      return { value: dropped, env: ctx.env };
    },
    dataset: {
      // Row reordering only: columns and types unchanged.
      propagateSchema: passThroughSchema,
      // Each sort-key arg (positional after the dataset) contributes
      // either a keyword col-ref or the col-refs of its computed-key
      // lambda body.
      referencedColumns: (data, basePath): readonly ColumnRefInArg[] => {
        const params = (data as { readonly params?: readonly unknown[] }).params ?? [];
        return params.slice(1).flatMap((keyAst, i) => sortKeyRefs(keyAst as AstInput, paramPath(basePath, i + 1)));
      },
    },
  });

type SortKeyClassification =
  | { readonly kind: 'col'; readonly column: string; readonly direction: SortDirection }
  | {
      readonly kind: 'lambda';
      readonly direction: SortDirection;
      readonly fnPath: string;
      readonly lambda: LambdaValue;
    };

/**
 * Classify a single sort-key arg into either a keyword column ref
 * (with direction) or a lambda body (with direction). The arg is
 * either a vector AST (tuple form) or a bare keyword / fn-expr.
 * Lambdas in the bare form default to ascending.
 *
 * Keyword form is preferred; bare-symbol shorthand is intentionally
 * not accepted — keeping lambdas explicit makes the row scope visible.
 */
const classifyKey = (keyAst: AstInput, keyPath: string, ctx: SpecialCtx): SortKeyClassification => {
  const shape = astShape(keyAst);
  if (shape.kind === 'vector') {
    return classifyVectorKey(shape.elements, keyPath, ctx);
  }
  if (shape.kind === 'keyword') {
    return { kind: 'col', column: shape.name, direction: 'asc' };
  }
  if (shape.kind === 'opCall' && shape.call.name === 'fn') {
    return classifyFn(shape.call, keyPath, 'asc', ctx);
  }
  throw new ShapeError(
    'expected a `[:col]` / `[:col :asc|:desc]` tuple, a bare keyword, or a `#(<expr>)` function',
    nodePos(ctx.spans, keyPath),
  );
};

/**
 * Rebuild a valid multi-key sort from a tuple the agent fused into one
 * bracket (`[:a :b]`, `[:a :b :asc]`). Each column keyword becomes its
 * own `[:col]` bracket; a trailing `:asc` / `:desc` stays on the last
 * column. Returns `null` when no element is a column keyword (nothing to
 * suggest), so the caller falls back to a generic recovery hint.
 */
const reconstructSortKeys = (elements: readonly AstInput[]): string | null => {
  const last = elements.at(-1);
  const trailingDir = last === undefined ? undefined : tryParseDirection(last);
  const columnNodes = trailingDir === undefined ? elements : elements.slice(0, -1);
  const cols = columnNodes.map((node) => {
    const shape = astShape(node);
    return shape.kind === 'keyword' ? `:${shape.name}` : null;
  });
  if (cols.length === 0 || cols.some((col) => col === null)) {
    return null;
  }
  const brackets = cols.map((col, i) =>
    trailingDir !== undefined && i === cols.length - 1 ? `[${col} :${trailingDir}]` : `[${col}]`,
  );
  return `(sort ${brackets.join(' ')})`;
};
const classifyVectorKey = (elements: readonly AstInput[], keyPath: string, ctx: SpecialCtx): SortKeyClassification => {
  if (elements.length === 0 || elements.length > 2) {
    throw new SortKeyError(
      `sort key tuple must have 1 or 2 elements; got ${elements.length}`,
      nodePos(ctx.spans, keyPath),
      reconstructSortKeys(elements),
    );
  }
  const [first, second] = elements;
  // A present second element that isn't a direction is almost always a
  // second column fused into one bracket (`[:a :b]`) — the #1 sort
  // mistake. Reconstruct the split form so the agent self-corrects.
  const direction = second === undefined ? 'asc' : tryParseDirection(second);
  if (direction === undefined) {
    throw new SortKeyError(
      DIRECTION_ERROR_MESSAGE,
      nodePos(ctx.spans, elemPath(keyPath, 1)),
      reconstructSortKeys(elements),
    );
  }
  const firstShape = astShape(first as AstInput);
  if (firstShape.kind === 'keyword') {
    return { kind: 'col', column: firstShape.name, direction };
  }
  if (firstShape.kind === 'opCall' && firstShape.call.name === 'fn') {
    return classifyFn(firstShape.call, elemPath(keyPath, 0), direction, ctx);
  }
  throw new ShapeError(
    'sort key tuple must start with a keyword column ref or a `#(<expr>)` function',
    nodePos(ctx.spans, elemPath(keyPath, 0)),
  );
};

/**
 * Extract the lambda's params/body/closureEnv and pack them into a
 * `LambdaValue` so the sort key compiles through the shared
 * `compileProjectorLambda` kernel (same arity gate as filter / derive).
 * The closureEnv is the current `ctx.env` because the `#(…)` reader
 * macro desugar runs at PARSE time — the body's free names should
 * resolve against the env active where `(sort …)` is being evaluated.
 */
const classifyFn = (
  call: { readonly name: string; readonly params?: readonly unknown[] },
  fnPath: string,
  direction: SortDirection,
  ctx: SpecialCtx,
): SortKeyClassification => {
  const fnParams = call.params as readonly [readonly { readonly $sym: string }[], AstInput] | undefined;
  if (fnParams === undefined || fnParams.length !== 2) {
    throw new ShapeError('expected `(fn [params…] body)` shape for sort computed key', nodePos(ctx.spans, fnPath));
  }
  const [paramSyms, body] = fnParams;
  return {
    kind: 'lambda',
    direction,
    fnPath,
    lambda: new LambdaValue(
      paramSyms.map((p) => p.$sym),
      body,
      ctx.env,
    ),
  };
};

const DIRECTION_ERROR_MESSAGE = 'sort direction must be `:asc` / `:desc` (or `"asc"` / `"desc"`)';

/**
 * Parse a single sort-direction AST node into `'asc' | 'desc'`, or
 * `undefined` if the node isn't a direction marker. Accepts both the
 * keyword surface (`:asc` / `:desc`) and the string-primitive surface
 * (`"asc"` / `"desc"`) — both forms are equally legal at the LLM-facing
 * boundary.
 */
const tryParseDirection = (node: unknown): SortDirection | undefined => {
  const shape = astShape(node);
  if (shape.kind === 'keyword' && (shape.name === 'asc' || shape.name === 'desc')) {
    return shape.name;
  }
  const direction = embeddedString(node);
  return direction === 'asc' || direction === 'desc' ? direction : undefined;
};
/**
 * Build a `SortKey` for a column on `dataset`, validating sortability
 * and attaching `dateAware` for temporal columns (so arquero's date
 * comparator wins over its lexical default).
 */
const buildKey = (
  column: string,
  direction: SortDirection,
  dataset: MaterializedDataset,
  callPos: SourcePos,
): SortKey => {
  const baseKey: SortKey = { column, direction };
  checkSortable(baseKey, dataset, callPos);
  const colType = dataset.schema.get(column);
  if (colType !== undefined && isTemporalColumn(colType)) {
    return { ...baseKey, dateAware: true };
  }
  return baseKey;
};

/**
 * Column types that arquero's `orderby` can compare without help.
 * Derived from the user-facing scalar kinds (the SSOT for recovery-hint
 * prose in `errors.ts`) plus the internal `nullable` kind, so the
 * "supported scalar types" wording in `UnsortableColumnTypeError` can
 * never drift from what this check actually accepts.
 */
const SORTABLE_KINDS = new Set<string>([...SCALAR_HINT_KINDS, 'nullable']);

/**
 * Whether a runtime cell can be put in a natural order by arquero's
 * default comparator. `null`/`undefined` are tolerated (skipped,
 * SQL-NULL style — `orderby` already sinks them consistently). A
 * `Dataset` or struct-object cell has no natural order.
 */
const isNaturallyOrderable = (cell: Cell): boolean => {
  if (cell === null || cell === undefined) {
    return true;
  }
  const t = typeof cell;
  return t === 'number' || t === 'string' || t === 'boolean' || cell instanceof Date;
};

/**
 * Verify a sort key's column can be ordered. Known-orderable schema
 * kinds pass immediately; positively-unorderable kinds fail up front.
 * For `unknown` columns (verb outputs without inferred types) we probe
 * runtime cells and only fail on genuinely non-orderable data.
 */
const checkSortable = (key: SortKey, dataset: MaterializedDataset, pos: SourcePos): void => {
  const colType = dataset.schema.get(key.column);
  if (colType === undefined) {
    throw new TypeMismatchError(
      `column \`${key.column}\` to exist on the threaded dataset (it is not in the upstream schema — check the column name, or add/keep it via an earlier columns/derive before sorting)`,
      'no such column',
      'sort',
      pos,
    );
  }
  if (SORTABLE_KINDS.has(colType.kind)) {
    return;
  }
  if (colType.kind !== 'unknown') {
    const subs = scalarSubfields(colType);
    throw new UnsortableColumnTypeError(key.column, describeColumnType(colType), pos, subs);
  }
  const offending = (dataset.columns[key.column] ?? []).find((cell) => !isNaturallyOrderable(cell));
  if (offending !== undefined) {
    throw new UnsortableColumnTypeError(key.column, describeValueKind(offending), pos);
  }
};
